import { Mail, Receipt } from 'lucide-react';
import EmptyState from '../EmptyState';
import { formatAmount, formatShortDay, getIcon, catColor, catTint } from '../../lib/constants';
import { useAmountsVisibility } from '../../context/AmountsVisibilityContext';
import { Skeleton } from '../ui/skeleton';

// "Últimos movimientos" on Inicio (redesign.md, Fase 4): the latest few in the
// selected period (GET /dashboard → recent_transactions), each row labeled
// "Categoría · Hoy/Ayer/25 oct", with a signed amount (− gasto, + ingreso).
export default function RecentMovements({ transactions = [], loading = false, onSeeAll, onAddTransaction }) {
  const { hidden } = useAmountsVisibility();

  return (
    <section data-testid="recent-transactions">
      <div className="flex items-center justify-between gap-3 mb-2">
        <h2 className="text-lg font-semibold tracking-tight text-white">Últimos movimientos</h2>
        {onSeeAll && transactions.length > 0 && (
          <button
            type="button"
            onClick={onSeeAll}
            data-testid="see-all-transactions"
            className="hit-44 text-sm font-medium text-violet-400 hover:text-violet-300 transition-colors"
          >
            Ver todo
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full rounded-xl bg-white/[0.04]" />)}
        </div>
      ) : transactions.length === 0 ? (
        // One compact row: the centered EmptyState sat right where Inicio's
        // fixed "Anotar gasto" bar is, so on a phone only its icon showed.
        <div className="flex items-center gap-3 py-3" data-testid="transactions-empty-state">
          <div className="w-10 h-10 shrink-0 rounded-xl bg-violet-500/10 flex items-center justify-center">
            <Receipt className="w-5 h-5 text-violet-400" />
          </div>
          <p className="text-sm text-slate-400">Aún no hay movimientos en este periodo. Anota uno con el botón de abajo.</p>
        </div>
      ) : (
        <ul className="divide-y divide-white/5">
          {transactions.map(txn => {
            const Icon = getIcon(txn.category_icon);
            const isIncome = txn.type === 'income';
            const isGoal = txn.type === 'goal';
            return (
              <li key={txn.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`recent-txn-${txn.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: catTint(txn.category_color) }}>
                    <Icon className="w-4 h-4" style={{ color: catColor(txn.category_color) }} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-200 flex items-center gap-1.5 min-w-0">
                      <span className="truncate">{txn.description || txn.category_name}</span>
                      {txn.source === 'email' && (
                        <Mail className="w-3 h-3 text-violet-400 shrink-0" aria-label="Detectado por Gmail" />
                      )}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {isGoal ? `Aporte a meta · ${txn.category_name}` : txn.category_name} · {formatShortDay(txn.date)}
                    </p>
                  </div>
                </div>
                {/* Signed, and only income gets color — a list that's
                    mostly red reads as alarm. */}
                <span className={`shrink-0 text-sm font-semibold metric-value ${isIncome ? 'text-emerald-400' : 'text-slate-200'}`}>
                  {isIncome ? '+' : '−'}{formatAmount(txn.amount, hidden)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
